<script lang="ts">
	import { Button, buttonVariants } from '$lib/components/ui/button';
	import { Check, Library } from 'lucide-svelte';
	import { Badge } from '$lib/components/ui/badge';
	import * as Card from '$lib/components/ui/card';
	import { bookDemo } from '$lib/state/global.svelte';
	import { goto } from '$app/navigation';
	import { MvmntBillingPeriod, cn, formatCurrency } from '$lib/utils.js';
	import { Slider } from '$lib/components/ui/slider/index.js';
	import type { ComponentElementsPlan } from '$lib/graphql/strapiApi';

	// TODO replace some of these with strapi options
	let {
		billingPeriod,
		plan,
		showAction = true,
		quantity = $bindable(),
		enableHover = false
	}: {
		billingPeriod: string;
		showAction?: boolean;
		plan: ComponentElementsPlan;
		quantity?: number;
		enableHover?: boolean;
	} = $props();

	let features = $derived(plan.features ?? []);

	let annually = $derived(billingPeriod === MvmntBillingPeriod.Annually);

	const choosePlan = (plan: ComponentElementsPlan) => {
		if (plan.name === 'Enterprise') {
			bookDemo();
		} else {
			goto(
				`${plan?.action?.url ?? '/signup?plan=free'}${plan.action?.url ? `&period=${billingPeriod}` : ''}`
			);
		}
	};

	let price = $derived(
		plan.price && plan.annualDiscount && annually
			? Math.round(plan.price * (1 - plan.annualDiscount))
			: plan.price
	);

	let totalAmount = $derived(
		Math.round(
			(billingPeriod === MvmntBillingPeriod.Annually ? 12 : 1) * (price ?? 0) * (quantity ?? 0)
		)
	);
</script>

{#if enableHover && showAction && plan.action}
	<div class="transition-transform hover:scale-105" onclick={() => choosePlan(plan)}>
		<Card.Root
			class={`flex h-[500px] w-[290px] flex-col justify-between ${plan.isRecommended ? 'shadow-[0px_0px_0px_4px_rgba(83,_139,_243,_0.10),_0px_1px_2px_-1px_rgba(28,_40,_64,_0.10),_0px_2px_4px_0px_rgba(28,_40,_64,_0.06)]' : 'hover:shadow-md'} transition-shadow cursor-pointer`}
		>
	<div>
		<Card.Header>
			<Card.Title class="flex items-center justify-between text-2xl"
				><h3>
					{plan.name}
					{#if plan.link}
						<a href={plan.link.url} class={cn(buttonVariants({ variant: 'ghost' }))}><Library /></a>
					{/if}
				</h3>
				{#if plan.isRecommended}<Badge class="bg-mvmnt text-black">Popular</Badge>{/if}</Card.Title
			>

			<div>
				<div class="flex flex-row items-center gap-x-2">
					<h2 class="text-4xl font-bold">
						{plan.price === null ? 'Custom' : `$${price}`}
					</h2>
					{#if plan.annualDiscount && annually && plan.price}
						<Badge variant="outline" class="h-6 rounded-md bg-gray-100 text-black">15% off</Badge>
					{/if}
				</div>
				<p class="pt-1 text-sm font-normal text-gray-500">
					{plan.pricePeriod ??
						`per user/month${plan.price !== 0 ? `, billed ${billingPeriod}` : ''}`}
				</p>
			</div>
		</Card.Header>
		<Card.Content>
			<Card.Description class="h-[88px] text-[15px] font-medium text-primary"
				>{plan.description}</Card.Description
			>

			<div class="flex flex-col gap-y-2 pt-6 text-sm">
				{#each features as feature}
					<p class="flex gap-x-1"><Check class="w-4" />{feature?.text}</p>
				{/each}
			</div>
		</Card.Content>
	</div>
	<Card.Footer class="">
		{#if showAction && plan.action}
			<Button class="w-full max-w-xs" onclick={() => choosePlan(plan)}>{plan.action.text}</Button>
		{/if}
		{#if quantity && price}
			<div class="mb-2 flex w-full flex-col items-center gap-y-2 text-lg">
				<p class="mb-2 text-center">
					{quantity} seat{quantity === 1 ? '' : 's'}
					x ${price}
					{billingPeriod === MvmntBillingPeriod.Annually ? 'x 12 months' : ''} <br />
					<b>{formatCurrency(totalAmount)} due today</b>
				</p>
				<Slider bind:value={quantity} min={1} max={50} step={1} type="single" />
			</div>
		{/if}
		{#if !showAction && !price}
			<div class={cn(buttonVariants({ variant: 'secondary' }), 'w-full')}>
				No credit card required
			</div>
		{/if}
		</Card.Footer>
	</Card.Root>
	</div>
{:else}
	<Card.Root
		class={`flex h-[500px] w-[290px] flex-col justify-between ${plan.isRecommended ? 'shadow-[0px_0px_0px_4px_rgba(83,_139,_243,_0.10),_0px_1px_2px_-1px_rgba(28,_40,_64,_0.10),_0px_2px_4px_0px_rgba(28,_40,_64,_0.06)]' : ''}`}
	>
	<div>
		<Card.Header>
			<Card.Title class="flex items-center justify-between text-2xl"
				><h3>
					{plan.name}
					{#if plan.link}
						<a href={plan.link.url} class={cn(buttonVariants({ variant: 'ghost' }))}><Library /></a>
					{/if}
				</h3>
				{#if plan.isRecommended}<Badge class="bg-mvmnt text-black">Popular</Badge>{/if}</Card.Title
			>

			<div>
				<div class="flex flex-row items-center gap-x-2">
					<h2 class="text-4xl font-bold">
						{plan.price === null ? 'Custom' : `$${price}`}
					</h2>
					{#if plan.annualDiscount && annually && plan.price}
						<Badge variant="outline" class="h-6 rounded-md bg-gray-100 text-black">15% off</Badge>
					{/if}
				</div>
				<p class="pt-1 text-sm font-normal text-gray-500">
					{plan.pricePeriod ??
						`per user/month${plan.price !== 0 ? `, billed ${billingPeriod}` : ''}`}
				</p>
			</div>
		</Card.Header>
		<Card.Content>
			<Card.Description class="h-[88px] text-[15px] font-medium text-primary"
				>{plan.description}</Card.Description
			>

			<div class="flex flex-col gap-y-2 pt-6 text-sm">
				{#each features as feature}
					<p class="flex gap-x-1"><Check class="w-4" />{feature?.text}</p>
				{/each}
			</div>
		</Card.Content>
	</div>
	<Card.Footer class="">
		{#if showAction && plan.action}
			<Button class="w-full max-w-xs" onclick={() => choosePlan(plan)}>{plan.action.text}</Button>
		{/if}
		{#if quantity && price}
			<div class="mb-2 flex w-full flex-col items-center gap-y-2 text-lg">
				<p class="mb-2 text-center">
					{quantity} seat{quantity === 1 ? '' : 's'}
					x ${price}
					{billingPeriod === MvmntBillingPeriod.Annually ? 'x 12 months' : ''} <br />
					<b>{formatCurrency(totalAmount)} due today</b>
				</p>
				<Slider bind:value={quantity} min={1} max={50} step={1} type="single" />
			</div>
		{/if}
		{#if !showAction && !price}
			<div class={cn(buttonVariants({ variant: 'secondary' }), 'w-full')}>
				No credit card required
			</div>
		{/if}
	</Card.Footer>
</Card.Root>
{/if}
